import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/pfc";
import { toast } from "sonner";

const TYPES = [
  { key: "general", label: "General" },
  { key: "advertising", label: "Advertising" },
  { key: "partnership", label: "Partnership" },
  { key: "media", label: "Media Request" },
];

export default function Contact() {
  const [params] = useSearchParams();
  const initialSubject = params.get("subject") || "";
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: initialSubject,
    message: "",
    inquiry_type: initialSubject ? "media" : "general",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll reply within 2 business days.");
      setForm({ name: "", email: "", subject: "", message: "", inquiry_type: "general" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inp = "w-full bg-ink-700 border border-white/10 text-white px-4 py-3 font-body focus:border-pfc-gold focus:outline-none focus:ring-1 focus:ring-pfc-gold";

  return (
    <div className="max-w-[1100px] mx-auto px-5 lg:px-10 py-14" data-testid="contact-page">
      <div className="grid lg:grid-cols-2 gap-12">
        <div>
          <div className="label text-pfc-gold mb-3">Contact</div>
          <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl text-white uppercase leading-[0.9]">
            Let’s <span className="gold-text">Talk</span> Business.
          </h1>
          <p className="text-white/70 mt-6 font-body text-lg">
            Advertising inquiries, media requests, partnerships, sponsored content — drop us a line.
          </p>
          <div className="mt-10 space-y-5 text-white/70 font-body" data-testid="contact-emails">
            <div className="pfc-card p-5 border-pfc-gold/40">
              <div className="label text-pfc-gold mb-2">Primary Contact</div>
              <a
                href="mailto:thepublicfigurescampaign@gmail.com"
                className="text-white font-display text-lg hover:text-pfc-gold transition break-all"
                data-testid="contact-primary-email"
              >
                thepublicfigurescampaign@gmail.com
              </a>
              <p className="text-white/55 text-xs mt-2 font-body">For all inquiries · responses within 2 business days</p>
            </div>
            <div>
              <div className="label text-pfc-gold mb-1">Advertising</div>
              <div>ads@publicfigurescampaign.com</div>
            </div>
            <div>
              <div className="label text-pfc-gold mb-1">Press & Media</div>
              <div>press@publicfigurescampaign.com</div>
            </div>
            <div>
              <div className="label text-pfc-gold mb-1">Partnerships</div>
              <div>partners@publicfigurescampaign.com</div>
            </div>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
          <div>
            <label className="label text-white/70 mb-2 block">Inquiry Type</label>
            <select value={form.inquiry_type} onChange={(e) => setForm({ ...form, inquiry_type: e.target.value })} className={inp} data-testid="contact-type">
              {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="label text-white/70 mb-2 block">Name</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} data-testid="contact-name" />
            </div>
            <div>
              <label className="label text-white/70 mb-2 block">Email</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} data-testid="contact-email" />
            </div>
          </div>
          <div>
            <label className="label text-white/70 mb-2 block">Subject</label>
            <input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inp} data-testid="contact-subject" />
          </div>
          <div>
            <label className="label text-white/70 mb-2 block">Message</label>
            <textarea required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inp} data-testid="contact-message" />
          </div>
          <button type="submit" disabled={loading} className="font-sub uppercase tracking-[0.25em] text-sm px-10 py-4 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition disabled:opacity-50" data-testid="contact-submit">
            {loading ? "Sending…" : "Send Message"}
          </button>
        </form>
      </div>
    </div>
  );
}
